// The un-scoped Scout thread: storage key, parse/serialize, and the in-flight label.
//
// The server reuses `panel-{email}` across reloads. The page has no history endpoint, so the
// lines live here. Junk in localStorage is an empty thread, never a throw — private mode or a
// quota must not take the tab with them.

export interface ChatLine {
  role: 'user' | 'assistant';
  text: string;
}

export function chatActorEmail(signedInEmail: string | null, viewingAs: string | null): string {
  return (viewingAs || signedInEmail || 'local').trim().toLowerCase();
}

export function unscopedChatStorageKey(actorEmail: string): string {
  return `scout-chat:${actorEmail.trim().toLowerCase()}`;
}

export function parseStoredChatLines(raw: string | null): ChatLine[] {
  if (!raw) return [];
  try {
    const parsed = JSON.parse(raw) as unknown;
    if (!Array.isArray(parsed)) return [];
    const lines: ChatLine[] = [];
    for (const entry of parsed) {
      if (!entry || typeof entry !== 'object') continue;
      const record = entry as { role?: unknown; text?: unknown };
      if (record.role !== 'user' && record.role !== 'assistant') continue;
      if (typeof record.text !== 'string') continue;
      lines.push({ role: record.role, text: record.text });
    }
    return lines;
  } catch {
    return [];
  }
}

export function serializeChatLines(lines: ChatLine[]): string {
  return JSON.stringify(lines);
}

export function readStoredChatLines(key: string): ChatLine[] {
  try {
    return parseStoredChatLines(window.localStorage.getItem(key));
  } catch {
    return [];
  }
}

export function writeStoredChatLines(key: string, lines: ChatLine[]): void {
  try {
    window.localStorage.setItem(key, serializeChatLines(lines));
  } catch {
    // Private mode or a quota. The conversation still works for this visit.
  }
}

// A dead text box for forty seconds reads as broken. Name the wait once it is no longer instant.
export function chatBusyLabel(elapsedSeconds: number): string {
  if (elapsedSeconds < 8) return 'Scout is answering…';
  if (elapsedSeconds < 40) return `Scout is still working (${elapsedSeconds}s)…`;
  return `Scout is still working (${elapsedSeconds}s) — a general question can take up to a minute.`;
}
